import { useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import type { FeatureKey, TenantFeatures } from '../../lib/superAdmin';
import { isFeatureEnabled } from '../../lib/superAdmin';
import { isEntitled } from '../../lib/entitlements';
import MenuManager from './MenuManager';
import TenantSettings from './TenantSettings';
import HoursManager from './HoursManager';
import SettingsPanel from './SettingsPanel';
import CustomersManager from './CustomersManager';
import WhatsAppSection from './WhatsAppSection';
import OrdersAdmin from './OrdersAdmin';
import DashboardOverview from './DashboardOverview';
import StripeSection from './StripeSection';
import LoyaltySection from './LoyaltySection';
import PromoCodesSection from './PromoCodesSection';
import NpsSection from './NpsSection';
import ClientBillingSection from './ClientBillingSection';
import OptionShowcase from './OptionShowcase';
import OptionsServicesHub from './OptionsServicesHub';
import NotificationBell from './NotificationBell';
import type { ModuleKey } from '../../lib/plans';

/**
 * Coque du back-office client (parité legacy) : barre latérale sombre + branding du resto,
 * navigation par sections, barre du haut, zone de contenu. On y branche les briques existantes
 * (commandes, menu, réglages, CRM, campagnes). Les sections sans brique encore portée affichent
 * un placeholder « bientôt » — à compléter incrément par incrément. Tout est mono-tenant.
 */
type SectionId =
  | 'dashboard'
  | 'orders'
  | 'menu'
  | 'payments'
  | 'hours'
  | 'brand'
  | 'customers'
  | 'options'
  | 'loyalty'
  | 'nps'
  | 'campaigns'
  | 'promos'
  | 'yumlink_controle'
  | 'billing'
  | 'settings';

interface NavItem {
  id: SectionId;
  icon: string;
  label: string;
}

const NAV: NavItem[] = [
  { id: 'dashboard', icon: '📊', label: 'Tableau de bord' },
  { id: 'orders', icon: '🧾', label: 'Commandes' },
  { id: 'menu', icon: '🍔', label: 'Carte & menu' },
  { id: 'payments', icon: '💳', label: 'Paiements Stripe' },
  { id: 'hours', icon: '🕒', label: 'Horaires' },
  { id: 'brand', icon: '🎨', label: 'Mon restaurant' },
  { id: 'customers', icon: '👥', label: 'Clients (CRM)' },
  { id: 'options', icon: '🧩', label: 'Options & Services' },
  { id: 'promos', icon: '🎟️', label: 'Codes promo' },
  { id: 'billing', icon: '🧾', label: 'Facturation' },
  { id: 'settings', icon: '⚙️', label: 'Paramètres' },
];

/**
 * Libellés des sections « option » — elles ne sont plus dans la nav (regroupées sous
 * « Options & Services »), mais restent accessibles via le bouton « Gérer » du hub.
 * Sert au titre d'en-tête + au bouton retour.
 */
const OPTION_SECTION_LABEL: Partial<Record<SectionId, string>> = {
  loyalty: 'Carte fidélité',
  nps: 'Avis Google',
  campaigns: 'Campagnes',
  yumlink_controle: 'YumLink Contrôle',
};

/** Sections dont l'affichage dépend d'un entitlement (table tenant_features). */
const SECTION_FEATURE: Partial<Record<SectionId, FeatureKey>> = {
  payments: 'online_payment',
  loyalty: 'loyalty',
  nps: 'reviews',
  campaigns: 'campaigns',
  yumlink_controle: 'yumlink_controle',
};

/**
 * Modules « OPTION » (payants) : quand ils ne sont PAS souscrits, on affiche une
 * VITRINE attractive (au lieu de masquer l'onglet) → incite à souscrire.
 * Map section → clé de module marketing (plans.ts / OptionShowcase).
 */
const OPTION_MODULE_KEY: Partial<Record<SectionId, ModuleKey>> = {
  loyalty: 'loyalty',
  nps: 'reviews',
  campaigns: 'campaigns',
  yumlink_controle: 'yumlink_controle',
};

/**
 * Une option est-elle SOUSCRITE ? (modèle freemium — décision produit 2026-07)
 * INACTIVE par défaut : active seulement si Lil & Co l'a explicitement accordée
 * (`tenant_features[key] === true`). Un resto Gratuit n'a donc aucune option ; il
 * la demande depuis « Options & Services ». Les restos existants qui doivent garder
 * leurs options reçoivent le flag explicite via SQL (migration d'accompagnement).
 */
function optionUnlocked(id: SectionId, features: TenantFeatures): boolean {
  const fk = SECTION_FEATURE[id];
  if (!fk) return true;
  return isFeatureEnabled(features, fk);
}

/** Section visible si non gâtée OU autorisée par Lil & Co (défaut autorisé). */
function sectionVisible(id: SectionId, features: TenantFeatures): boolean {
  const key = SECTION_FEATURE[id];
  return !key || isEntitled(features, key);
}

export default function AdminShell({
  tenant,
  features = {},
  adminEmail,
  onSignOut,
}: {
  tenant: Tenant;
  features?: TenantFeatures;
  adminEmail?: string;
  onSignOut?: () => void;
}) {
  const [section, setSection] = useState<SectionId>('orders');
  // Nav : les modules OPTION restent TOUJOURS visibles (ils affichent une vitrine
  // quand non souscrits, pour inciter à souscrire). Les autres modules gâtés
  // (ex. paiement) restent masqués s'ils ne sont pas autorisés.
  const nav = NAV.filter((it) => (OPTION_MODULE_KEY[it.id] ? true : sectionVisible(it.id, features)));
  const current = nav.find((n) => n.id === section);
  // Les sections « option » (fidélité, avis…) ne sont plus dans la nav : leur titre
  // vient d'OPTION_SECTION_LABEL quand on les ouvre depuis le hub « Options & Services ».
  const headerLabel = current?.label ?? OPTION_SECTION_LABEL[section] ?? '';
  const shopUrl = `/r/${tenant.slug}`;
  const tabletUrl = `/r/${tenant.slug}/tablet`;

  const brand = tenant.primary_color || tenant.brand_color || '#E65100';
  const logo = tenant.logo_url ? (
    <img src={tenant.logo_url} alt="" className="h-10 w-10 rounded-lg object-contain" />
  ) : (
    <span
      className="flex h-10 w-10 items-center justify-center rounded-lg text-xl font-black text-white"
      style={{ background: brand }}
    >
      {tenant.name.slice(0, 1)}
    </span>
  );

  return (
    <div className="flex min-h-screen w-full bg-slate-50 text-slate-800">
      {/* Barre latérale (white-label : branding du resto) */}
      <aside className="flex w-60 flex-shrink-0 flex-col bg-slate-900 text-slate-300">
        <div className="flex items-center gap-2.5 border-b border-slate-800 p-5">
          {logo}
          <div className="min-w-0">
            <div className="truncate text-sm font-extrabold text-white">{tenant.name}</div>
            {tenant.city && <div className="truncate text-xs text-slate-400">{tenant.city}</div>}
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          {nav.map((it) => (
            <button
              key={it.id}
              onClick={() => setSection(it.id)}
              className={
                'mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition ' +
                (section === it.id ? 'bg-white text-slate-900' : 'text-slate-300 hover:bg-slate-800')
              }
            >
              <span className="text-lg">{it.icon}</span>
              {it.label}
            </button>
          ))}
          {/* « Mode tablette » et « Voir ma boutique » sont déjà en haut à droite (header). */}
        </nav>
      </aside>

      {/* Zone principale */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-baseline gap-2">
            <h1 className="text-xl font-bold" style={{ color: brand }}>
              {headerLabel}
            </h1>
            <span className="text-sm text-slate-400">/{tenant.slug}</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell tenantId={tenant.id} />
            <a
              href={tabletUrl}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              📱 Mode tablette
            </a>
            <a
              href={shopUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
            >
              🛍️ Voir ma boutique
            </a>
            {onSignOut && (
              <button
                onClick={onSignOut}
                title={adminEmail ? `Connecté : ${adminEmail}` : 'Déconnexion'}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
              >
                Déconnexion
              </button>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <AdminSection id={section} tenant={tenant} features={features} onOpen={setSection} />
        </main>
      </div>
    </div>
  );
}

function Soon({ label }: { label: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <div className="mb-2 text-3xl">🛠️</div>
      <div className="font-bold text-slate-700">{label}</div>
      <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
        Section bientôt disponible dans le SaaS — en cours de portage depuis l'admin actuel.
      </p>
    </div>
  );
}

function Dashboard({ tenant }: { tenant: Tenant }) {
  return (
    <div className="flex flex-col gap-5">
      {/* Parité legacy BB/FK : KPIs + deltas réels + CA 7j + top produits + alertes réelles. */}
      <DashboardOverview tenant={tenant} />
      <OrdersAdmin tenantId={tenant.id} currency={tenant.currency} tenantName={tenant.name} />
    </div>
  );
}

function BackToOptions({ onOpen }: { onOpen: (id: SectionId) => void }) {
  return (
    <button
      onClick={() => onOpen('options')}
      className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"
    >
      ← Options &amp; Services
    </button>
  );
}

function AdminSection({
  id,
  tenant,
  features = {},
  onOpen,
}: {
  id: SectionId;
  tenant: Tenant;
  features?: TenantFeatures;
  onOpen: (id: SectionId) => void;
}) {
  // Hub central « Options & Services ».
  if (id === 'options') {
    return <OptionsServicesHub tenant={tenant} features={features} onOpen={(s) => onOpen(s as SectionId)} />;
  }

  const moduleKey = OPTION_MODULE_KEY[id];

  // Module OPTION non souscrit → vitrine (incite à souscrire) + retour au hub.
  if (moduleKey && !optionUnlocked(id, features)) {
    return (
      <div>
        <BackToOptions onOpen={onOpen} />
        <OptionShowcase moduleKey={moduleKey} tenant={{ name: tenant.name, slug: tenant.slug }} />
      </div>
    );
  }
  // Module gâté non-option (ex. paiement) non autorisé → garde défensive.
  if (!moduleKey && !sectionVisible(id, features)) {
    return <Soon label="Module non inclus dans votre offre" />;
  }

  const content = (() => {
    switch (id) {
      case 'dashboard':
        return <Dashboard tenant={tenant} />;
      case 'orders':
        return <OrdersAdmin tenantId={tenant.id} currency={tenant.currency} tenantName={tenant.name} />;
      case 'menu':
        return <MenuManager tenant={tenant} />;
      case 'brand':
        return <TenantSettings tenant={tenant} defaultOpen />;
      case 'hours':
        return <HoursManager tenant={tenant} />;
      case 'settings':
        return <SettingsPanel tenant={tenant} />;
      case 'billing':
        return <ClientBillingSection tenant={tenant} />;
      case 'customers':
        return <CustomersManager tenant={tenant} />;
      case 'campaigns':
        return <WhatsAppSection tenants={[tenant]} />;
      case 'payments':
        return <StripeSection tenantId={tenant.id} currency={tenant.currency} />;
      case 'loyalty':
        return <LoyaltySection tenantId={tenant.id} currency={tenant.currency} />;
      case 'nps':
        return <NpsSection tenantId={tenant.id} />;
      case 'promos':
        return <PromoCodesSection tenant={tenant} />;
      case 'yumlink_controle':
        return <Soon label="YumLink Contrôle — module en préparation (votre option est active)" />;
      default:
        return <Soon label="Section" />;
    }
  })();

  // Sections « option » (ouvertes via « Gérer » du hub) : bouton retour au hub.
  if (moduleKey) {
    return (
      <div>
        <BackToOptions onOpen={onOpen} />
        {content}
      </div>
    );
  }
  return content;
}
